import { useAiRecommendationsStore } from '@cobliteam/hooks';
import { permissionsConfig } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import usePermissions from '@cobliteam/permissions/src/hooks/usePermissions.tsx';
import Button from '@cobliteam/ui/components/Button';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { AiSparkleIcon } from '@cobliteam/ui-business';
import useAiRecommendationCta from '@cobliteam/ui-business/components/AiRecommendationBanner/useAiRecommendationCta';
import CriticalityTag from '@cobliteam/ui-business/components/CriticalityTag';

const criticalityOrder: Record<string, number> = { critical: 0, urgent: 1, important: 2, suggestive: 3 };

const AiRecommendationFloatingCard = () => {
  const { aiRecommendations } = useFeatureFlags();
  const { isAccessAllowed } = usePermissions();

  const { recommendations, improvementRecommendations, progress, isError, floatingCardDismissed, dismissFloatingCard } = useAiRecommendationsStore();

  const allowedRecommendations = recommendations.filter((item) => {
    return isAccessAllowed(permissionsConfig[item.journeyId]);
  });

  const allowedImprovementRecommendations = improvementRecommendations.filter((item) => {
    return isAccessAllowed(permissionsConfig[item.journeyId]);
  });

  const allRecs = [...allowedRecommendations, ...allowedImprovementRecommendations];
  const topRec = [...allRecs].sort((a, b) => (criticalityOrder[a.criticality] ?? 99) - (criticalityOrder[b.criticality] ?? 99))[0] ?? null;

  const { ctaButton } = useAiRecommendationCta({
    component: 'ai-recommendation-floating-card',
    journeyId: topRec?.journeyId ?? '',
    aiPrompt: topRec?.aiPrompt,
  });

  if (!(aiRecommendations && topRec) || floatingCardDismissed || isError) return null;

  const allCompleted = progress.totalJourneys > 0 && progress.completedJourneys >= progress.totalJourneys;

  return (
    <div className='fixed bottom-4 left-[calc(var(--sidebar-width-icon)+1rem)] z-[1003] w-[358px]'>
      <div
        className='relative flex w-full overflow-hidden rounded-2xl shadow-lg'
        style={{
          border: '1px solid transparent',
          backgroundImage: 'linear-gradient(#F8F7FD, #F8F7FD), linear-gradient(90deg, #001BB8 0%, #9847EE 100%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
        }}
      >
        {/* Gradient top border */}
        <div className='absolute top-0 right-0 left-0 h-1' style={{ background: 'linear-gradient(to right, #001BB8, #9847EE)' }} />

        <div className='flex w-full flex-col gap-3 px-4 pt-5 pb-4'>
          {/* Icon + Tag + Dismiss row */}
          <div className='flex items-center gap-3'>
            {/* Gradient-bordered sparkles icon */}
            <div
              className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl'
              style={{
                border: '2.5px solid transparent',
                backgroundImage: 'linear-gradient(#F8F7FD, #F8F7FD), linear-gradient(90deg, #001BB8 0%, #9847EE 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
              }}
            >
              <AiSparkleIcon size={20} />
            </div>

            {/* Criticality Tag */}
            <div className='flex-1'>{topRec.criticality && <CriticalityTag criticality={topRec.criticality} />}</div>

            {/* Dismiss */}
            <Button variant='ghost' color='default' size='condensed' icon={<Icon name='x' size='sm' />} onClick={() => dismissFloatingCard()} aria-label='Fechar' />
          </div>

          {/* Title */}
          <Typography variant='description-title'>{topRec.journeyName}</Typography>

          {/* Description */}
          <Typography variant='description-list-default' color='secondary' className='text-start'>
            {topRec.description}
          </Typography>

          {/* Bottom: time + CTA */}
          {!allCompleted && (
            <div className='flex items-center justify-between gap-2'>
              {topRec.estimatedMinutes > 0 && <Tag variant='outlined' color='default' label={`${topRec.estimatedMinutes} min`} icon={<Icon name='clock' size='xs' />} />}
              {ctaButton}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiRecommendationFloatingCard;
